import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import 'react-tiny-fab/dist/styles.css';
import { groupBy, isEmpty } from 'lodash';
import { DateRangeSelect } from '../components/DateRangeSelect.component';
import { humanizeKind } from '../activities/utils/translations';
import { ViewActions } from './components/viewActions.button';
import { Filters } from './filters/Filters.component';
import { useReservations } from './reservations.context';
import { ActivityFilter } from './filters/ActivityFilter.component';
import { ReservationGrid } from './reservationGrid.component';
import { NextDaysStripSelect } from '../components/nextDaysStripSelect.component';
//import { GoToTop } from '../components/fab/GoToTop.fab';
import { containsText, ofActivities } from './filters/predicates';
import dayjs from 'dayjs';
//import { useLocation } from 'react-router-dom';

/**
 * Styles pour la vue des réservations actives
 * 
 * Styles appliqués :
 * - Layout responsive avec breakpoints
 * - Scroll horizontal pour les sélecteurs de période
 * - Largeur minimale pour les tableaux sur mobile
 */
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(1),

    '& > *': {
      margin: theme.spacing(1),
    },
  },
  periodSelectContainer: {
    [theme.breakpoints.down('md')]: {
      maxWidth: "100vw",
      overflowX: "scroll"
    },
  },
  tableContainer: {
    [theme.breakpoints.down('xs')]: {
      maxWidth: "100vw",
      overflowX: "scroll"
    },
  },
  table: {
    [theme.breakpoints.down('xs')]: {
      minWidth: "2000px",
    },
  }
}));

/**
 * Vue des réservations actives (commencées et en cours d'activité)
 * 
 * Cette vue permet de :
 * - Consulter les réservations avec statut "started" et "in-activity"
 * - Filtrer par période, texte et activités
 * - Gérer les mises à jour en lot
 * - Afficher les réservations groupées par type d'activité
 * 
 * Logique métier :
 * - Filtrage automatique par statut "started"
 * - Support des départs au jour 2 (setMatchesStartDayTwo)
 * - Regroupement par type d'activité (canoe, supervised)
 * - Gestion des mises à jour avec boutons Annuler/Valider
 * - Interface responsive pour mobile et desktop
 * 
 * Fonctionnalités :
 * - Sélection de période avec NextDaysStripSelect et DateRangeSelect
 * - Filtrage par texte libre
 * - Filtrage par activités sélectionnées
 * - Actions en lot sur les réservations
 * - Affichage groupé par type d'activité
 * 
 * @returns Composant React de la vue des réservations actives
 */
export function ReservationsStartedView() {
  const classes = useStyles();
  const {
    updates,
    updatesClear,
    updatesSubmit,
    reservations,
    refreshReservations,
    filterDays,
    filterText,
    setFilterDays,
    setFilterText,
    setFilterDaysToDay,
    setMatchesStartDayTwo
  } = useReservations();
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  //const location = useLocation();

  useEffect(() => {
    if(filterDays.start === undefined) {
      filterDays.start = dayjs();
    }
    if(filterDays.end === undefined) {
      filterDays.end = dayjs();
    }
    setMatchesStartDayTwo(1);// find departures at day two
    refreshReservations();
  }, [refreshReservations, filterDays, setMatchesStartDayTwo]);

  const filtered = (reservations || [])
    .filter(containsText(filterText))
    .filter(ofActivities(selectedActivities));
  const byKindReservations = groupBy(filtered, res => res.activity.kind);
  const kinds = Object.keys(byKindReservations) as CRM.ActivityKind[];

  return (
    <div className={classes.root}>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        flexWrap={'wrap'}
      >
        <Box display={'flex'} alignItems={'flex-start'} className={classes.periodSelectContainer}>
          <NextDaysStripSelect
            onChange={setFilterDaysToDay}
            value={filterDays}
          />
          {!isMobile && <DateRangeSelect range={filterDays} onChange={setFilterDays} />}
        </Box>
        {isMobile && <DateRangeSelect range={filterDays} onChange={setFilterDays} />}
        <Filters filterText={filterText} onChange={setFilterText} />
      </Box>
      <Divider />
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <ActivityFilter
          value={selectedActivities}
          onChange={setSelectedActivities}
          nowrapLabels={true}
        />
        <ViewActions reservations={reservations} />
      </Box>
      <Divider />
      <Box className={classes.tableContainer}>
        {reservations === undefined && <CircularProgress size="1.2em" />}
        {reservations && kinds.length === 0 && (
          <span>Aucune réservation trouvée</span>
        )}
        {kinds.map(kind => (
          <Box mb={7} mt={2} key={kind} className={classes.table}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography variant={'h6'}>
                <b>{humanizeKind(kind)}</b>
              </Typography>
              {!isEmpty(updates) && (
                <ButtonGroup
                  size={'small'}
                  aria-label="contained primary button group"
                >
                  <Button variant="contained" onClick={updatesClear}>
                    Annuler
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={updatesSubmit}
                  >
                    Valider
                  </Button>
                </ButtonGroup>
              )}
            </Box>
            <ReservationGrid
              status={
                /*( location.pathname.includes('wait-to-paid') || location.pathname.includes('refunds') ) ? 
                undefined :*/'started'}
              kind={kind}
              rows={byKindReservations[kind]}
              active={1}
            />
          </Box>
        ))}
      </Box>
      {/*<GoToTop />*/}
    </div>
  );
}
